import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import NewNavbar from './components/NewNavbar';
import Footer from './components/Footer';
import FloatingButtons from './components/FloatingButtons';
import ScrollToTop from './components/ScrollToTop';
import { PageLoadingSkeleton } from './components/ui/skeleton-loaders';

/**
 * Route guard: redirects to /admin/login if no admin_token in localStorage.
 * Wraps every protected admin route at the router level so no child
 * component renders even for a single frame without auth.
 */
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/admin/login?next=${next}`} replace />;
  }
  return children;
};

// Lazy load page components for code splitting
const GatewayLanding = lazy(() => import('./pages/GatewayLanding'));
const ListingsPage = lazy(() => import('./pages/ListingsPage'));
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const FounderPage = lazy(() => import('./pages/FounderPage'));
const HealthcareOpportunities = lazy(() => import('./pages/HealthcareOpportunities'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPost })));
const AgentPage = lazy(() => import('./pages/AgentPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));

// Admin Pages - Lazy loaded
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ListingForm = lazy(() => import('./pages/ListingForm'));
const TestimonialsAdmin = lazy(() => import('./pages/admin/TestimonialsAdmin'));
const TeamAdmin = lazy(() => import('./pages/admin/TeamAdmin'));
const WhatsAppAdmin = lazy(() => import('./pages/admin/WhatsAppAdmin'));
const LeadsAdmin = lazy(() => import('./pages/admin/LeadsAdmin'));
const IndustryTypesAdmin = lazy(() => import('./pages/admin/IndustryTypesAdmin'));
const BlogAdmin = lazy(() => import('./pages/admin/BlogAdmin'));
const NdaAdmin = lazy(() => import('./pages/admin/NdaAdmin'));
const ValuationsAdmin = lazy(() => import('./pages/admin/ValuationsAdmin'));
const AgentMyProfile = lazy(() => import('./pages/AgentMyProfile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const BuyerForgotPassword = lazy(() => import('./pages/BuyerForgotPassword'));
const BuyerResetPassword = lazy(() => import('./pages/BuyerResetPassword'));

// Valuation Calculator
const ValuationLanding = lazy(() => import('./pages/ValuationLanding'));
const ValuationWizard = lazy(() => import('./pages/ValuationWizard'));
const ValuationResult = lazy(() => import('./pages/ValuationResult'));

import { Toaster } from './components/ui/toaster';
import { BuyerAuthProvider } from './contexts/BuyerAuthContext';
const BuyerPortal = lazy(() => import('./pages/BuyerPortal'));
const BuyerVerifyEmail = lazy(() => import('./pages/BuyerVerifyEmail'));
const SellerIntakePage = lazy(() => import('./pages/SellerIntakePage'));

function App() {
  return (
    <BuyerAuthProvider>
    <BrowserRouter>
      <div className="App">
        <ScrollToTop />
        <Suspense fallback={<PageLoadingSkeleton />}>
          <Routes>
            {/* Gateway Landing Page - New Main Homepage */}
            <Route path="/" element={
              <>
                <NewNavbar />
                <GatewayLanding />
                <Footer />
              </>
            } />

            {/* Commercial Homepage - alias of the main homepage (merged 2026-06-16) */}
            <Route path="/commercial" element={
              <>
                <NewNavbar />
                <GatewayLanding />
                <Footer />
                <FloatingButtons />
              </>
            } />

            {/* Public Routes */}
            <Route path="/listings" element={
              <>
                <NewNavbar />
                <ListingsPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/listing/:id" element={
              <>
                <NewNavbar />
                <ListingDetailPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/contact" element={
              <>
                <NewNavbar />
                <ContactPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/about/*" element={
              <>
                <NewNavbar />
                <AboutPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/services/*" element={
              <>
                <NewNavbar />
                <ServicesPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/founder" element={
              <>
                <NewNavbar />
                <FounderPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/healthcare-opportunities" element={
              <>
                <NewNavbar />
                <HealthcareOpportunities />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/privacy-policy" element={
              <>
                <NewNavbar />
                <PrivacyPolicy />
                <Footer />
              </>
            } />
            <Route path="/terms-of-use" element={
              <>
                <NewNavbar />
                <TermsOfUse />
                <Footer />
              </>
            } />
            <Route path="/location/:location" element={
              <>
                <NewNavbar />
                <LocationPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/blog" element={
              <>
                <NewNavbar />
                <BlogPage />
                <Footer />
                <FloatingButtons />
              </>
            } />
            <Route path="/blog/:slug" element={
              <>
                <NewNavbar />
                <BlogPost />
                <Footer />
                <FloatingButtons />
              </>
            } />
            
            {/* Admin Routes - Public (no auth required) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/buyer/forgot-password" element={<BuyerForgotPassword />} />
            <Route path="/buyer/reset-password" element={<BuyerResetPassword />} />
            <Route path="/buyer/verify-email" element={<BuyerVerifyEmail />} />
            <Route path="/seller/intake/:token" element={<SellerIntakePage />} />

            {/* Admin Routes - Protected (auth required) */}
            <Route path="/admin/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/listings/new" element={<ProtectedRoute><ListingForm /></ProtectedRoute>} />
            <Route path="/admin/listings/edit/:id" element={<ProtectedRoute><ListingForm /></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute><TestimonialsAdmin /></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute><TeamAdmin /></ProtectedRoute>} />
            <Route path="/admin/whatsapp" element={<ProtectedRoute><WhatsAppAdmin /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><LeadsAdmin /></ProtectedRoute>} />
            <Route path="/admin/industry-types" element={<ProtectedRoute><IndustryTypesAdmin /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><BlogAdmin /></ProtectedRoute>} />
            <Route path="/admin/ndas" element={<ProtectedRoute><NdaAdmin /></ProtectedRoute>} />
            <Route path="/admin/valuations" element={<ProtectedRoute><ValuationsAdmin /></ProtectedRoute>} />
            <Route path="/agent/profile" element={<ProtectedRoute><AgentMyProfile /></ProtectedRoute>} />

            {/* Buyer Portal */}
            <Route path="/buyer/portal" element={
              <>
                <NewNavbar />
                <BuyerPortal />
                <Footer />
              </>
            } />

            {/* Valuation Calculator */}
            <Route path="/what-is-my-business-worth" element={
              <>
                <NewNavbar />
                <ValuationLanding />
                <Footer />
              </>
            } />
            <Route path="/valuation/start" element={
              <>
                <NewNavbar />
                <ValuationWizard />
                <Footer />
              </>
            } />
            <Route path="/valuation/result/:id" element={
              <>
                <NewNavbar />
                <ValuationResult />
                <Footer />
              </>
            } />

            {/* Agent Personal Page Route - must be last to not conflict with other routes */}
            <Route path="/:agentSlug" element={
              <>
                <NewNavbar />
                <AgentPage />
                <Footer />
              </>
            } />
          </Routes>
        </Suspense>
        <Toaster />
      </div>
    </BrowserRouter>
    </BuyerAuthProvider>
  );
}

export default App;